import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { ReliabilityAdapter, ReliabilityAlertRule, ReliabilityCheckTarget, ReliabilityRunbook, ReliabilityService } from './reliability.service';

@Component({
  selector: 'app-reliability-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reliability-dashboard.component.html',
  styleUrls: ['./reliability-dashboard.component.scss'],
})
export class ReliabilityDashboardComponent implements OnInit {
  activeTab: 'dashboard' | 'adapters' | 'incidents' | 'checks' | 'rules' | 'runbooks' = 'dashboard';
  loading = false;
  error = '';
  toast = '';
  dashboard: any = null;
  adapters: ReliabilityAdapter[] = [];
  checkTargets: ReliabilityCheckTarget[] = [];
  incidents: any[] = [];
  healthChecks: any[] = [];
  alertRules: ReliabilityAlertRule[] = [];
  runbooks: ReliabilityRunbook[] = [];
  selectedIncident: any = null;
  incidentReport: any = null;
  incidentAssignee = '';
  incidentComment = '';
  testingAdapterId: number | null = null;
  testingCheckTargetId: number | null = null;
  selectedAdapterForTargets: ReliabilityAdapter | null = null;
  bootstrapBaseUrl = environment.apiHost;
  bootstrapEnvironment = 'Production';
  incidentFilter = {
    status: '',
    severity: '',
    adapterType: '',
  };

  adapterForm: ReliabilityAdapter = this.blankAdapter();
  checkTargetForm: ReliabilityCheckTarget = this.blankCheckTarget();
  alertRuleForm: ReliabilityAlertRule = this.blankAlertRule();
  runbookForm: ReliabilityRunbook = this.blankRunbook();

  readonly adapterTypes = ['GRMIS', 'ASTERISK', 'POSTGRESQL', 'AI', 'WHATSAPP', 'EMAIL', 'SMS', 'SECURITY_AUDIT', 'CUSTOM'];
  readonly authTypes = ['NONE', 'BEARER'];
  readonly checkModes = ['HTTP', 'JDBC'];
  readonly httpMethods = ['GET', 'HEAD', 'POST'];
  readonly severities = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  readonly incidentStatuses = ['OPEN', 'ACKNOWLEDGED', 'INVESTIGATING', 'RESOLVED'];

  constructor(private readonly reliabilityService: ReliabilityService) {}

  async ngOnInit(): Promise<void> {
    await this.loadAll();
  }

  async loadAll(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      await Promise.all([
        this.loadDashboard(),
        this.loadAdapters(),
        this.loadIncidents(),
        this.loadHealthChecks(),
        this.loadAlertRules(),
        this.loadRunbooks(),
      ]);
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Reliability data could not be loaded';
    } finally {
      this.loading = false;
    }
  }

  async loadDashboard(): Promise<void> {
    this.dashboard = await this.reliabilityService.dashboard();
  }

  async loadAdapters(): Promise<void> {
    this.adapters = await this.reliabilityService.adapters();
  }

  async loadIncidents(): Promise<void> {
    this.incidents = await this.reliabilityService.incidents(this.incidentFilter);
  }

  async loadHealthChecks(): Promise<void> {
    this.healthChecks = await this.reliabilityService.healthChecks();
  }

  async loadAlertRules(): Promise<void> {
    this.alertRules = await this.reliabilityService.alertRules();
  }

  async loadRunbooks(): Promise<void> {
    this.runbooks = await this.reliabilityService.runbooks();
  }

  editAdapter(adapter: ReliabilityAdapter): void {
    this.adapterForm = { ...adapter, authToken: '', authPassword: '' };
    this.selectAdapterTargets(adapter);
    this.activeTab = 'adapters';
  }

  newAdapter(type = 'GRMIS'): void {
    this.adapterForm = this.blankAdapter(type);
    this.selectedAdapterForTargets = null;
    this.checkTargets = [];
    this.checkTargetForm = this.blankCheckTarget();
    this.activeTab = 'adapters';
  }

  async selectAdapterTargets(adapter: ReliabilityAdapter): Promise<void> {
    this.selectedAdapterForTargets = adapter;
    this.checkTargetForm = this.blankCheckTarget(adapter);
    if (!adapter.adapterId) {
      this.checkTargets = [];
      return;
    }
    this.checkTargets = await this.reliabilityService.checkTargets(adapter.adapterId);
  }

  editCheckTarget(target: ReliabilityCheckTarget): void {
    this.checkTargetForm = { ...target, authToken: '', authPassword: '' };
  }

  newCheckTarget(): void {
    this.checkTargetForm = this.blankCheckTarget(this.selectedAdapterForTargets || undefined);
  }

  editAlertRule(rule: ReliabilityAlertRule): void {
    this.alertRuleForm = { ...rule };
    this.activeTab = 'rules';
  }

  newAlertRule(type = 'GRMIS'): void {
    this.alertRuleForm = this.blankAlertRule(type);
    this.activeTab = 'rules';
  }

  editRunbook(runbook: ReliabilityRunbook): void {
    this.runbookForm = { ...runbook };
    this.activeTab = 'runbooks';
  }

  newRunbook(type = 'GRMIS'): void {
    this.runbookForm = this.blankRunbook(type);
    this.activeTab = 'runbooks';
  }

  async saveAdapter(): Promise<void> {
    this.error = '';
    try {
      await this.reliabilityService.saveAdapter(this.adapterForm);
      this.showToast('Adapter saved');
      this.adapterForm = this.blankAdapter(this.adapterForm.adapterType);
      this.selectedAdapterForTargets = null;
      this.checkTargets = [];
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Adapter save failed';
    }
  }

  async deleteAdapter(adapter: ReliabilityAdapter): Promise<void> {
    if (!adapter.adapterId || !confirm(`Remove adapter "${adapter.adapterName}"?`)) return;
    await this.reliabilityService.deleteAdapter(adapter.adapterId);
    this.showToast('Adapter removed');
    await this.loadAll();
  }

  async runCheck(adapter: ReliabilityAdapter): Promise<void> {
    if (!adapter.adapterId) return;
    this.testingAdapterId = adapter.adapterId;
    this.error = '';
    try {
      await this.reliabilityService.runCheck(adapter.adapterId);
      this.showToast('Health check completed');
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Health check failed';
    } finally {
      this.testingAdapterId = null;
    }
  }

  async saveCheckTarget(): Promise<void> {
    if (!this.selectedAdapterForTargets?.adapterId) {
      this.error = 'Select an adapter before adding check targets';
      return;
    }
    this.error = '';
    try {
      await this.reliabilityService.saveCheckTarget(this.selectedAdapterForTargets.adapterId, this.checkTargetForm);
      this.showToast('Check target saved');
      await this.selectAdapterTargets(this.selectedAdapterForTargets);
      await this.loadDashboard();
      await this.loadHealthChecks();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Check target save failed';
    }
  }

  async deleteCheckTarget(target: ReliabilityCheckTarget): Promise<void> {
    if (!this.selectedAdapterForTargets?.adapterId || !target.checkTargetId || !confirm(`Remove check target "${target.checkName}"?`)) return;
    await this.reliabilityService.deleteCheckTarget(this.selectedAdapterForTargets.adapterId, target.checkTargetId);
    this.showToast('Check target removed');
    await this.selectAdapterTargets(this.selectedAdapterForTargets);
    await this.loadAll();
  }

  async runCheckTarget(target: ReliabilityCheckTarget): Promise<void> {
    if (!this.selectedAdapterForTargets?.adapterId || !target.checkTargetId) return;
    this.testingCheckTargetId = target.checkTargetId;
    this.error = '';
    try {
      await this.reliabilityService.runCheckTarget(this.selectedAdapterForTargets.adapterId, target.checkTargetId);
      this.showToast('Target check completed');
      await this.selectAdapterTargets(this.selectedAdapterForTargets);
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Target check failed';
    } finally {
      this.testingCheckTargetId = null;
    }
  }

  async saveAlertRule(): Promise<void> {
    this.error = '';
    try {
      await this.reliabilityService.saveAlertRule(this.alertRuleForm);
      this.showToast('Alert rule saved');
      this.alertRuleForm = this.blankAlertRule(this.alertRuleForm.adapterType);
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Alert rule save failed';
    }
  }

  async deleteAlertRule(rule: ReliabilityAlertRule): Promise<void> {
    if (!rule.alertRuleId || !confirm(`Remove alert rule for ${rule.adapterType}?`)) return;
    await this.reliabilityService.deleteAlertRule(rule.alertRuleId);
    this.showToast('Alert rule removed');
    await this.loadAll();
  }

  async saveRunbook(): Promise<void> {
    this.error = '';
    try {
      await this.reliabilityService.saveRunbook(this.runbookForm);
      this.showToast('Runbook saved');
      this.runbookForm = this.blankRunbook(this.runbookForm.adapterType);
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Runbook save failed';
    }
  }

  async deleteRunbook(runbook: ReliabilityRunbook): Promise<void> {
    if (!runbook.runbookId || !confirm(`Remove runbook "${runbook.title}"?`)) return;
    await this.reliabilityService.deleteRunbook(runbook.runbookId);
    this.showToast('Runbook removed');
    await this.loadAll();
  }

  async acknowledge(incident: any): Promise<void> {
    await this.reliabilityService.acknowledge(incident.incidentId);
    await this.loadIncidents();
    this.showToast('Incident acknowledged');
  }

  async resolve(incident: any): Promise<void> {
    await this.reliabilityService.resolve(incident.incidentId);
    await this.loadAll();
    this.showToast('Incident resolved');
  }

  async openIncident(incident: any): Promise<void> {
    if (!incident?.incidentId) return;
    this.selectedIncident = await this.reliabilityService.incidentDetail(incident.incidentId);
    this.incidentAssignee = this.selectedIncident.assignedTo || '';
    this.incidentComment = '';
    this.incidentReport = null;
  }

  closeIncident(): void {
    this.selectedIncident = null;
    this.incidentReport = null;
  }

  async generateIncidentReport(): Promise<void> {
    if (!this.selectedIncident?.incidentId) return;
    this.incidentReport = await this.reliabilityService.incidentReport(this.selectedIncident.incidentId);
  }

  async assignSelectedIncident(): Promise<void> {
    if (!this.selectedIncident?.incidentId || !this.incidentAssignee.trim()) return;
    await this.reliabilityService.assignIncident(this.selectedIncident.incidentId, this.incidentAssignee.trim());
    await this.openIncident(this.selectedIncident);
    await this.loadIncidents();
    this.showToast('Incident assigned');
  }

  async addSelectedIncidentComment(): Promise<void> {
    if (!this.selectedIncident?.incidentId || !this.incidentComment.trim()) return;
    this.selectedIncident = await this.reliabilityService.addIncidentComment(this.selectedIncident.incidentId, this.incidentComment.trim());
    this.incidentComment = '';
    this.showToast('Comment added');
  }

  async bootstrapDefaults(): Promise<void> {
    this.error = '';
    try {
      const result = await this.reliabilityService.bootstrapDefaults(this.bootstrapBaseUrl, this.bootstrapEnvironment);
      this.showToast(`Defaults prepared: ${result.createdAdapters || 0} adapters, ${result.createdAlertRules || 0} rules, ${result.createdRunbooks || 0} runbooks`);
      await this.loadAll();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Default setup failed';
    }
  }

  statusClass(status: string | undefined): string {
    const value = (status || 'UNKNOWN').toLowerCase();
    if (['healthy', 'resolved', 'stable'].includes(value)) return 'ok';
    if (['slow', 'degraded', 'acknowledged', 'investigating', 'watch'].includes(value)) return 'warn';
    if (['down', 'failed', 'open', 'critical', 'high', 'action_required'].includes(value)) return 'bad';
    return 'muted';
  }

  private blankAdapter(type = 'GRMIS'): ReliabilityAdapter {
    return {
      adapterName: type === 'GRMIS' ? 'GRMIS Health' : `${type} Adapter`,
      adapterType: type,
      environmentName: 'Production',
      baseUrl: type === 'GRMIS' ? environment.apiHost : '',
      healthCheckUrl: type === 'GRMIS' ? '/api/v1/reliability/probes/grmis' : '',
      authType: 'NONE',
      checkIntervalSeconds: 60,
      timeoutSeconds: 5,
      enabled: true,
    };
  }

  private blankCheckTarget(adapter?: ReliabilityAdapter): ReliabilityCheckTarget {
    return {
      adapterId: adapter?.adapterId,
      checkName: adapter?.adapterName ? `${adapter.adapterName} primary health check` : 'Primary health check',
      checkMode: 'HTTP',
      urlOrCommand: adapter?.healthCheckUrl || '/',
      httpMethod: 'GET',
      authType: adapter?.authType || 'NONE',
      timeoutSeconds: adapter?.timeoutSeconds || 5,
      expectedStatusCodes: '200-299',
      required: true,
      enabled: true,
      displayOrder: 0,
    };
  }

  private blankAlertRule(type = 'GRMIS'): ReliabilityAlertRule {
    return {
      adapterType: type,
      checkType: type,
      severity: 'HIGH',
      failureThreshold: 3,
      timeWindowMinutes: 5,
      notificationChannel: 'DASHBOARD',
      enabled: true,
    };
  }

  private blankRunbook(type = 'GRMIS'): ReliabilityRunbook {
    return {
      adapterType: type,
      incidentType: `${type}_HEALTH_FAILURE`,
      title: `${type} health failure`,
      description: '',
      steps: '1. Confirm the service is reachable.\n2. Check recent logs.\n3. Verify network and credentials.\n4. Escalate if the issue remains.',
      severity: 'HIGH',
      enabled: true,
    };
  }

  private showToast(message: string): void {
    this.toast = message;
    setTimeout(() => this.toast = '', 2400);
  }
}
